import {
  Checkbox,
  Divider,
  Grid,
  IconButton,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  FileIcon,
  FileNameAndIconContainer,
  FolderIcon,
} from '@components/FileBrowser/StyledComponents';
import RowButtonWithContextMenu from '@components/FileBrowser/Components/FileBrowserRowContextMenu';

interface IFileBrowserRowProps {
  userFileUrl: string;
  createdAt: string;
  isDirectory: boolean;
  fileSize: number | null;
  handleClick: (isDirectory: boolean, userFileUrl: string) => void;
  handleDownload: () => void;
  handleDelete: () => void;
  selected?: boolean;
}

const getHumanReadableFileSize = (fileSize: number) => {
  if (fileSize < 1000) {
    return `${fileSize}B`;
  }

  if (fileSize < 1000000) {
    return `${Math.round(fileSize / 1000)}KB`;
  }

  if (fileSize < 1000000000) {
    return `${Math.round(fileSize / 1000000)}MB`;
  }

  return `${Math.round(fileSize / 1000000000)}GB`;
};

export const FileBrowserRow = ({
  userFileUrl: propUserFileUrl,
  isDirectory: propIsDirectory,
  fileSize,
  handleDelete,
  createdAt,
  handleClick,
  selected,
  handleDownload,
}: IFileBrowserRowProps) => (
  <>
    <RowButtonWithContextMenu
      handleDownload={handleDownload}
      isDirectory={propIsDirectory}
    >
      <Grid container key={propUserFileUrl}>
        <ListItemButton
          disableGutters
          onClick={() => handleClick(propIsDirectory, propUserFileUrl)}
          sx={(theme) => ({
            px: { xs: 1.5, md: 2 },
            py: 1.25,
            transition: theme.transitions.create('background-color', {
              duration: theme.transitions.duration.shortest,
            }),
            '&.Mui-selected': { bgcolor: 'primary.light' },
            '&.Mui-selected:hover': { bgcolor: 'primary.light' },
          })}
          selected={selected}
        >
          <Grid item xs={12} sm={4} display="flex">
            <ListItemIcon sx={{ minWidth: '30px' }}>
              <Checkbox
                edge={false}
                checked={selected}
                tabIndex={-1}
                disableRipple
                disabled={propIsDirectory}
                size="small"
                sx={{ padding: 0 }}
              />
            </ListItemIcon>
            <FileNameAndIconContainer>
              {propIsDirectory ? <FolderIcon /> : <FileIcon />}
              <div style={{ display: 'grid' }}>
                <Typography variant="caption" noWrap>
                  {propUserFileUrl}
                </Typography>
              </div>
            </FileNameAndIconContainer>
          </Grid>
          <Grid item sm={4} display={{ xs: 'none', sm: 'block' }}>
            <Typography variant="caption" display={{ xs: 'none', md: 'block' }}>
              {new Date(createdAt).toString()}
            </Typography>
            <Typography variant="caption" display={{ xs: 'block', md: 'none' }}>
              {new Date(createdAt).toDateString()}
            </Typography>
          </Grid>
          <Grid item sm={2} display={{ xs: 'none', sm: 'block' }}>
            <Typography variant="caption">
              {fileSize ? getHumanReadableFileSize(fileSize) : ''}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={2}
            onClick={(e) => {
              e.stopPropagation();
              if (!propIsDirectory) handleDelete();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (propIsDirectory) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-end', sm: 'center' },
              px: 1,
              py: { xs: 0.5, sm: 0 },
              cursor: propIsDirectory ? 'not-allowed' : 'pointer',
              userSelect: 'none',

              // make it feel like an action zone
              '&:hover': {
                bgcolor: propIsDirectory ? 'transparent' : 'action.hover',
              },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'error.main',
                outlineOffset: '2px',
                borderRadius: 1,
              },
            }}
          >
            <Tooltip
              title={propIsDirectory ? 'Cannot delete folders' : 'Delete'}
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={propIsDirectory}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation(); // prevent row selection/open
                    if (!propIsDirectory) handleDelete(); // <-- execute delete
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </ListItemButton>
      </Grid>
    </RowButtonWithContextMenu>
    <Divider />
  </>
);
